// G.3 — Container & intermodal panel.
//
// Surfaces the freight-model deep extensions captured on the deal:
//   - G.3b: Container ports (POL / POD)
//   - G.3c: Pre-carriage (előfutás) + on-carriage (utófutás) road legs
//   - G.3d: Intermodal connection points (handover events between modes)
//
// All three sections render only when at least one of their fields is set —
// otherwise the panel collapses to a small empty hint. Display-only here;
// editing happens via the right-side parameter popovers in the editor.
import React from "react";
import { Anchor, Truck, Repeat2, MapPin, Clock } from "lucide-react";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #0a0a0a)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const MODE_COLOR = {
  road: "#1769aa",
  sea: "#0a7fc4",
  rail: "#7e3aa8",
  air: "#0a8754",
  multimodal: "#b45309",
};

const ModeChip = ({ mode, testid }) => {
  if (!mode) return null;
  const c = MODE_COLOR[mode] || "#475569";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded"
      style={{ background: `${c}15`, color: c, border: `1px solid ${c}55`, ...mono(9, c, 700, "0.6px"), textTransform: "uppercase" }}
      data-testid={testid}
    >
      {mode}
    </span>
  );
};

const _hasAny = (obj) => obj && typeof obj === "object" && Object.values(obj).some((v) => v != null && v !== "");

const PortBlock = ({ label, port, kind }) => {
  if (!_hasAny(port)) return null;
  return (
    <div
      className="rounded p-3"
      style={{ background: "rgba(10,127,196,0.06)", border: "1px solid rgba(10,127,196,0.20)" }}
      data-testid={`g3b-port-${kind}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Anchor size={14} color="#0a7fc4" />
        <span style={{ ...mono(9, "#0a7fc4", 700, "0.6px"), textTransform: "uppercase" }}>{label}</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {port.un_locode && (
          <span style={mono(11, "#0a0a0a", 700, "0.4px")} data-testid={`g3b-port-${kind}-locode`}>
            {port.un_locode}
          </span>
        )}
        {port.name && (
          <span style={mono(11, "#0a0a0a", 400)} data-testid={`g3b-port-${kind}-name`}>
            {port.name}
          </span>
        )}
        {port.country && (
          <span style={mono(10, "#6b7388", 600)} data-testid={`g3b-port-${kind}-country`}>
            {port.country}
          </span>
        )}
      </div>
    </div>
  );
};

const CarriageBlock = ({ label, leg, kind }) => {
  if (!_hasAny(leg)) return null;
  return (
    <div
      className="rounded p-3"
      style={{ background: "rgba(126,58,168,0.05)", border: "1px solid rgba(126,58,168,0.20)" }}
      data-testid={`g3c-carriage-${kind}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Truck size={14} color="#7e3aa8" />
          <span style={{ ...mono(9, "#7e3aa8", 700, "0.6px"), textTransform: "uppercase" }}>{label}</span>
          <ModeChip mode={leg.mode} testid={`g3c-carriage-${kind}-mode`} />
        </div>
        {leg.status && (
          <span style={{ ...mono(9, "#475569", 700, "0.4px"), textTransform: "uppercase" }} data-testid={`g3c-carriage-${kind}-status`}>
            {leg.status}
          </span>
        )}
      </div>
      {(leg.pickup_location || leg.dropoff_location) && (
        <div className="flex items-center gap-2 mb-1">
          <MapPin size={11} color="#6b7388" />
          <span style={mono(11, "#0a0a0a", 400)}>
            {leg.pickup_location || "—"}
            <span style={{ color: "#6b7388", margin: "0 6px" }}>→</span>
            {leg.dropoff_location || "—"}
          </span>
        </div>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
        {leg.carrier_name && (
          <span style={mono(10, "#0a0a0a", 600)} data-testid={`g3c-carriage-${kind}-carrier`}>
            {leg.carrier_name}
          </span>
        )}
        {leg.vehicle_plate && (
          <span style={{ ...mono(10, "#0a0a0a", 700, "0.6px") }} data-testid={`g3c-carriage-${kind}-plate`}>
            {leg.vehicle_plate}
          </span>
        )}
        {leg.cost_amount != null && (
          <span style={mono(10, "#0a8754", 700)} data-testid={`g3c-carriage-${kind}-cost`}>
            {leg.cost_amount} {leg.cost_currency || ""}
          </span>
        )}
      </div>
      {leg.notes && (
        <div className="mt-1.5" style={mono(10, "#6b7388", 400)}>
          {leg.notes}
        </div>
      )}
    </div>
  );
};

const ConnectionPointRow = ({ cp, idx }) => {
  if (!cp || typeof cp !== "object") return null;
  return (
    <div
      className="flex items-center gap-3 py-2 px-3 rounded"
      style={{ background: "rgba(180,83,9,0.05)", border: "1px solid rgba(180,83,9,0.20)" }}
      data-testid={`g3d-connection-point-${idx}`}
    >
      <Repeat2 size={14} color="#b45309" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <ModeChip mode={cp.from_mode} testid={`g3d-connection-point-${idx}-from`} />
          <span style={{ color: "#6b7388" }}>→</span>
          <ModeChip mode={cp.to_mode} testid={`g3d-connection-point-${idx}-to`} />
          <span style={mono(11, "#0a0a0a", 700)} data-testid={`g3d-connection-point-${idx}-location`}>
            {cp.location || "—"}
          </span>
          {cp.country && (
            <span style={mono(10, "#6b7388", 600)}>{cp.country}</span>
          )}
        </div>
        {(cp.handover_at || cp.notes) && (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            {cp.handover_at && (
              <span className="inline-flex items-center gap-1" style={mono(10, "#6b7388", 400)}>
                <Clock size={10} /> {String(cp.handover_at).replace("T", " ").slice(0, 16)}
              </span>
            )}
            {cp.notes && <span style={mono(10, "#6b7388", 400)}>{cp.notes}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default function ContainerIntermodalPanel({ deal }) {
  if (!deal) return null;
  const pol = deal.port_of_loading;
  const pod = deal.port_of_discharge;
  const pre = deal.pre_carriage;
  const onc = deal.on_carriage;
  const cps = Array.isArray(deal.intermodal_connection_points) ? deal.intermodal_connection_points : [];

  const hasPorts = _hasAny(pol) || _hasAny(pod);
  const hasCarriage = _hasAny(pre) || _hasAny(onc);
  const hasCps = cps.length > 0;
  // Render the panel only when something is set (or when this is a container deal).
  if (!hasPorts && !hasCarriage && !hasCps && !deal.is_container_movement) return null;

  return (
    <div
      className="rounded-lg p-4 mb-4"
      style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid rgba(0,0,0,0.08)" }}
      data-testid="g3-container-intermodal-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: "0.4px" }}>
          Container & intermodal
        </h3>
        {deal.is_container_movement && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded"
            style={{ background: "rgba(10,135,84,0.10)", border: "1px solid rgba(10,135,84,0.35)", ...mono(9, "#0a8754", 700, "0.6px") }}
            data-testid="g3-container-flag-chip"
          >
            CONTAINER MOVEMENT
          </span>
        )}
      </div>

      {hasPorts && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3" data-testid="g3b-ports-grid">
          <PortBlock label="Port of loading (POL)" port={pol} kind="loading" />
          <PortBlock label="Port of discharge (POD)" port={pod} kind="discharge" />
        </div>
      )}

      {hasCarriage && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3" data-testid="g3c-carriage-grid">
          <CarriageBlock label="Pre-carriage (előfutás)" leg={pre} kind="pre" />
          <CarriageBlock label="On-carriage (utófutás)" leg={onc} kind="on" />
        </div>
      )}

      {hasCps && (
        <div data-testid="g3d-connection-points-list">
          <div style={{ ...mono(9, "#b45309", 700, "0.6px"), textTransform: "uppercase", marginBottom: 6 }}>
            Intermodal connection points ({cps.length})
          </div>
          <div className="flex flex-col gap-2">
            {cps.map((cp, i) => <ConnectionPointRow key={i} cp={cp} idx={i} />)}
          </div>
        </div>
      )}

      {!hasPorts && !hasCarriage && !hasCps && deal.is_container_movement && (
        <div style={mono(11, "#6b7388", 400)} data-testid="g3-container-empty-hint">
          Container deal flagged — add port-of-loading, pre/on-carriage legs and connection points from the right-side parameter panel in the editor.
        </div>
      )}
    </div>
  );
}
